import React, { useState, useEffect, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import { toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useParams, Link } from "react-router-dom";
import Select, { SingleValue } from "react-select";
import { Addresses } from "types";
import { getAuthCookie } from "../../utils/authCookies";

const mapContainerStyle = {
    width: "100%",
    height: "450px",
};

const center = {
    lat: 40.712776, // Default latitude (e.g., New York City)
    lng: -74.005974, // Default longitude
};

const SignageViewRemovalRequest = () => {
    const { id } = useParams(); // get ID from URL
    const token = getAuthCookie("sessionToken");
    const userId = getAuthCookie("userId");
    const userName = getAuthCookie("userName");
    const [preview, setPreview] = useState<string | null>(null);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [quantity, setQuantity] = useState(1);
    const [addressObjects, setAddressObjects] = useState<Addresses[]>([]);
    const [showAllocateModal, setShowAllocateModal] = useState(false);
    const [removalOrder, setRemovalOrder] = useState<any>(null);

    const [selectedLocation, setSelectedLocation] = useState<{
        lat: number;
        lng: number;
    } | null>(null);
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string, // Replace with your API key
    });

    const navigate = useNavigate();

    useEffect(() => {
        return () => {
            if (preview) URL.revokeObjectURL(preview);
        };
    }, [preview]);

    const addressOptions =
        addressObjects !== null
            ? addressObjects?.map((address) => ({
                ...address,
                value: address._id,
                label: address.label,
            }))
            : [];

    const officesOptions = [
        { value: "office1", label: "Office 1" },
        { value: "office2", label: "Office 2" },
        { value: "office3", label: "Office 3" },
    ];

    const locationOptions = [
        { value: "new_york", label: "New York" },
        { value: "los_angeles", label: "Los Angeles" },
        { value: "chicago", label: "Chicago" },
        { value: "houston", label: "Houston" },
    ];

    const [selectedBillingAddress, setSelectedBillingAddress] =
        useState<Addresses | null>(null);

    const [selectedInstallationAddress, setSelectedInstallationAddress] =
        useState<Addresses | null>(null);

    useEffect(() => {
        const fetchRemovalOrder = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                const data = await response.json();

                if (!response.ok) {
                    toast.error(data.error || "Failed to fetch removal order");
                    return;
                }

                setRemovalOrder(data);
                console.log("Fetched removal order:", data);
            } catch (err) {
                toast.error("Something went wrong while fetching removal order.");
                console.error(err);
            }
        };

        if (id) {
            fetchRemovalOrder();
        }
    }, [id]);

    useEffect(() => {
        if (removalOrder?.removal_details?.removal_address?.latitude && removalOrder?.removal_details?.removal_address?.longitude) {
            setSelectedLocation({
                lat: removalOrder.removal_details.removal_address.latitude,
                lng: removalOrder.removal_details.removal_address.longitude,
            });
        }
    }, [removalOrder]);

    const formik = useFormik({
        initialValues: {
            order_number: "",
            priority: "",
            scheduled_date: "",
            removal_date: "",
            jobs_today: 0,
            restock_warehouse: "",
            installer: "",
            billing_address: "",
            removal_address: "",
            isSameAsBilling: false,
            billingAddressId: "",
            removalAddressId: "",
            location: "",
            products: "",
            instructions: "",
            payment_method: "",
            image: null,
        },
        validationSchema: Yup.object({
            installer: Yup.string().required("Installer is required"),
            installation_date: Yup.string().required("Installation date is required"),
            billing_address: Yup.string().required("Billing address is required"),
            installation_address: Yup.string().required(
                "Installation address is required"
            ),
            image: Yup.mixed().required("Image is required"),
        }),
        onSubmit: async (values) => {
            setFormSubmitting(true);
            try {
                // Example file upload handling
                const imageUrl = values.image ? await uploadFile(values.image) : null;

                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/categories`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            ...values,
                            image: imageUrl,
                        }),
                    }
                );

                if (!response.ok) {
                    const error = await response.json();
                    throw new Error(error.message || "Failed to create installation");
                }

                navigate("/agent/categories", {
                    state: { successMessage: "Installation added successfully!" },
                });
                toast.success("Installation added successfully!");
            } catch (error: any) {
                console.error(error);
                toast.error(`Error: ${error.message}`);
            } finally {
                setFormSubmitting(false);
                setUploadProgress(0);
            }
        },
    });

    const uploadFile = async (file: File): Promise<string> => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("uploadDir", "categories");

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("POST", `${process.env.REACT_APP_API_URL}/images/upload`, true);
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    setUploadProgress((event.loaded / event.total) * 100);
                }
            };

            xhr.onload = () => {
                if (xhr.status === 200) {
                    const response = JSON.parse(xhr.responseText);
                    resolve(response.path);
                } else {
                    reject(new Error("Failed to upload file"));
                }
            };

            xhr.onerror = () => reject(new Error("Network error during file upload"));
            xhr.send(formData);
        });
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.currentTarget.files?.[0];
        if (file) {
            formik.setFieldValue("image", file);
            setPreview(URL.createObjectURL(file));
        }
    };

    const handleLocationChange = (
        selectedOption: SingleValue<{ value: string; label: string }>
    ) => {
        formik.setFieldValue("location", selectedOption?.value || "");
    };

    const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();
            setSelectedLocation({ lat, lng });
            formik.setFieldValue("location", { lat, lng });
        }
    }, []);

    if (!isLoaded) return <div>Loading...</div>;

    return (
        <div className="content">
            <div className="card">
                <div className="card-body">
                    <h2 className="text-center">View Removal Request </h2>
                    <form onSubmit={formik.handleSubmit}>
                        <div className="row mb-3">
                            <h3>Request Details</h3>
                            <table className="table white-table">
                                <tr>
                                    <th>Installation Order ID</th>
                                    <td>{removalOrder?.order_number || "—"}</td>

                                    <th>Removal Request ID</th>
                                    <td>{removalOrder?.order_number || "—"}</td>
                                </tr>

                                <tr>
                                    <th>Company Name</th>
                                    <td>{removalOrder?.signage_company_name || "—"}</td>

                                    <th>Office</th>
                                    <td>{removalOrder?.office?.officeName || "—"}</td>
                                </tr>
                                <tr>
                                    <th>Agent Name</th>
                                    <td>{removalOrder?.agent_name || "—"}</td>

                                    <th>Email</th>
                                    <td>{removalOrder?.user_email || "—"}</td>
                                </tr><tr>
                                    <th>Phone Number</th>
                                    <td>{removalOrder?.user_phone || "—"}</td>

                                    <th>&nbsp;</th>
                                    <td>&nbsp;</td>
                                </tr>
                                <tr>
                                    <th>Billing Address</th>
                                    <td>{removalOrder?.billing_address?.addressLine1}, {removalOrder?.billing_address?.city},  {removalOrder?.billing_address?.state}</td>

                                    <th>Removal Address</th>
                                    <td>{removalOrder?.removal_details?.removal_address?.addressLine1}, {removalOrder?.removal_details?.removal_address?.city}, {removalOrder?.removal_details?.removal_address?.state}</td>
                                </tr>
                                <tr>
                                    <th>Scheduled Date</th>
                                    <td>{removalOrder?.removal_details?.scheduled_date
                                        ? new Date(removalOrder.removal_details.scheduled_date).toLocaleDateString()
                                        : "—"}</td>

                                    {/* <th>Jobs Today</th>
                                    <td>3</td> */}

                                    <th>Assigned To</th>
                                    <td>{removalOrder?.removal_details?.installer_name || "—"}</td>
                                </tr>
                                <tr>
                                    {/* <th>Assigned To</th>
                                    <td>John</td> */}

                                    <th>Priority</th>
                                    <td>{removalOrder?.priority === 1
                                        ? "Normal"
                                        : removalOrder?.priority === 2
                                            ? "High Priority"
                                            : removalOrder?.priority === 3
                                                ? "Critical"
                                                : "—"}</td>
                                </tr>
                            </table>
                        </div>
                        <div className="row mb-3">
                            <h4>Normal Products</h4>
                        </div>
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <strong>Warehouse :</strong> Warehouse 1
                                {/* <input type="text" value="Warehouse 1" className="form-control" disabled /> */}
                            </div>
                        </div>

                        <div className="row mb-5">
                            <div className="col-md-12">
                                <table className="table table-bordered">
                                    <thead className="bg-primary-subtle">
                                        <tr>
                                            <th className="text-center">Product Name</th>
                                            <th className="text-center">SKU</th>
                                            <th className="text-center">Quantity </th>
                                            <th className="text-center">Product Location Status</th>
                                            <th className="text-center">Re-stock </th>
                                            <th className="text-center">Write Off </th>
                                            <th className="text-center">Reason</th>
                                            <th className="text-center">Bin Location</th>
                                            <th className="text-center">Note</th>
                                        </tr>
                                    </thead>
                                    {/* <tbody>
                                        <tr>
                                            <td className="text-center">Panel XYZ</td>
                                            <td className="text-center">PNL-XYZ</td>
                                            <td className="text-center">1</td>
                                            <td className="text-center">On Site</td>
                                            <td className="text-center">1</td>
                                            <td className="text-center">0</td>
                                            <td className="text-center">Damaged</td>
                                            <td className="text-center">Aisle 2</td>
                                            <td className="text-center">No issues</td>
                                        </tr>
                                    </tbody> */}

                                    <tbody>
                                        {removalOrder?.line_items
                                            ?.filter((item: any) => !item.is_rotational)
                                            .map((item: any, index: number) => (
                                                <tr key={index}>
                                                    <td className="text-center">{item.product_name || "—"}</td>
                                                    <td className="text-center">{item.sku || "—"}</td>
                                                    <td className="text-center">{item.quantity_to_be_removed}</td>
                                                    <td className="text-center">{item.current_status || "—"}</td>
                                                    <td className="text-center">{item.restock_quantity}</td>
                                                    <td className="text-center">{item.writeoff_quantity}</td>
                                                    <td className="text-center">{item.reason_to_writeoff || "—"}</td>
                                                    <td className="text-center">—</td>
                                                    <td className="text-center">—</td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="row mb-3">
                            <h4>Assets</h4>
                        </div>
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <strong>Warehouse :</strong> Warehouse 1
                                {/* <input type="text" value="Warehouse 1" className="form-control" disabled /> */}
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col-md-12">
                                <table className="table table-bordered">
                                    <thead className="bg-primary-subtle">
                                        <tr>
                                            <th className="text-center">Product Name</th>
                                            <th className="text-center">SKU</th>
                                            <th className="text-center">Serial No. </th>
                                            {/* <th className="text-center">Return </th> */}
                                            <th className="text-center">Product Status</th>
                                            <th className="text-center">Re-stock </th>
                                            <th className="text-center">Write Off </th>
                                            <th className="text-center">Reason</th>
                                            <th className="text-center">Bin Location</th>
                                            <th className="text-center">Note</th>
                                        </tr>
                                    </thead>
                                    {/* <tbody>
                                        <tr>
                                            <td className="text-center">Post ABC</td>
                                            <td className="text-center">PST-ABC</td>
                                            <td className="text-center">SN-12345</td>
                                            <td className="text-center">Yes</td>
                                            <td className="text-center">With Supplier</td>
                                            <td className="text-center">0</td>
                                            <td className="text-center">1</td>
                                            <td className="text-center">Lost</td>
                                            <td className="text-center">B-3</td>
                                            <td className="text-center">Missing serial tag</td>
                                        </tr>
                                    </tbody> */}
                                    <tbody>
                                        {removalOrder?.line_items
                                            ?.filter((item: any) => item.is_rotational && Array.isArray(item.serial_ids))
                                            ?.flatMap((item: any) =>
                                                item.serial_codes.map((serial: string, i: number) => (
                                                    <tr key={`${item.product_id}-${serial}`}>
                                                        <td className="text-center">{item.product_name || "—"}</td>
                                                        <td className="text-center">{item.sku || "—"}</td>
                                                        <td className="text-center">{serial}</td>
                                                        <td className="text-center">{item.current_status || "—"}</td>
                                                        <td className="text-center">{item.restock_quantity}</td>
                                                        <td className="text-center">{item.writeoff_quantity}</td>
                                                        <td className="text-center">{item.reason_to_writeoff || "—"}</td>
                                                        <td className="text-center">—</td>
                                                        <td className="text-center">—</td>
                                                    </tr>
                                                ))
                                            )}
                                    </tbody>

                                </table>
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div><h3 className="mb-3 text-center">Map View</h3></div>
                            <div className="col-md-12">
                                <div style={mapContainerStyle}>
                                    <GoogleMap
                                        mapContainerStyle={mapContainerStyle}
                                        zoom={10}
                                        center={selectedLocation || center}
                                    >
                                        {selectedLocation && <Marker position={selectedLocation} />}
                                    </GoogleMap>
                                </div>
                            </div>
                        </div>

                        <div className="row mb-3 border-top border-bottom">
                            <div className="col-md-6 pt-3">
                                <div className="mb-3">
                                    <h4>Instructions</h4>
                                    <textarea
                                        name="instructions"
                                        id="instructions"
                                        className="form-control"
                                        value={removalOrder?.removal_details.removal_notes}
                                        // onChange={formik.handleChange}
                                        // onBlur={formik.handleBlur}
                                        rows={4}
                                        readOnly
                                    ></textarea>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <table className="calculation-table mb-3">
                                    <tr>
                                        <th>Removal Fee</th>
                                        <td>$50.00</td>
                                    </tr>
                                    <tr>
                                        <th>Subtotal</th>
                                        <td>$5.00</td>
                                    </tr>
                                    <tr>
                                        <th>Tax</th>
                                        <td>$5.00</td>
                                    </tr>
                                    <tr>
                                        <th>Total</th>
                                        <td>$60.00</td>
                                    </tr>
                                </table>
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col-md-8">
                                <h4>Proof of Removal</h4>
                                <p>Uploaded Photos</p>
                                <img src="/assets/images/task.png" alt="" className="img-thumbnail me-1" />
                                <img src="/assets/images/task.png" alt="" className="img-thumbnail" />
                            </div>
                        </div>

                        <Link to={`/signage/orders`} className="btn btn-success float-end">
                            Back to Orders
                        </Link>
                    </form>
                </div>
            </div >
        </div >
    );
};

export default SignageViewRemovalRequest;
